/**
 * @fileoverview Ask in English — the shared analytics entry point
 * (MOR-906 §7.8)
 *
 * One box on the Dashboard, Widgets and Reports tabs. The model writes a
 * specification, never numbers: the answer is handed to whichever builder
 * matches its shape, which runs it through the deterministic aggregator. So
 * every answer is exact, editable and saveable.
 *
 * @component
 * @example
 * <AskAnalyticsBox orgId={orgId} onAnswer={setPendingSpec} />
 */

import { useState } from "react";
import { useAction } from "convex/react";
import { format } from "date-fns";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimePivotSpec } from "@cvx/timesheets/savedPivotReports/schema";
import type { TimeWidgetSpec } from "@cvx/timesheets/savedTimeReports/schema";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";

/** The artefact an English question resolved to. */
export type AnalyticsAnswer =
  | { shape: "widget"; title: string; spec: TimeWidgetSpec }
  | { shape: "pivot"; title: string; spec: TimePivotSpec };

const CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

export function AskAnalyticsBox({
  orgId,
  onAnswer,
  className,
}: {
  orgId: Id<"organizations">;
  /** Called with the spec; the page routes it to the matching builder. */
  onAnswer: (answer: AnalyticsAnswer) => void;
  className?: string;
}) {
  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  const nlReport = useAction(api.timesheets.draftAi.nlReport);

  const handleAsk = async () => {
    if (!question.trim() || running) return;
    setRunning(true);
    try {
      const answer = await nlReport({
        orgId,
        question,
        today: format(new Date(), "yyyy-MM-dd"),
      });
      onAnswer(answer);
      setQuestion("");
      toast.success(
        answer.shape === "pivot"
          ? `Built the "${answer.title}" table — tweak it or save it.`
          : `Built "${answer.title}" — tweak the blocks or save it.`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not build that report."));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className={cn(CARD_CLASS, "flex items-center gap-2 p-3", className)}>
      <Sparkles className="size-4 shrink-0 text-muted-foreground" />
      <Input
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void handleAsk();
          }
        }}
        placeholder='Ask for a widget or a table: "Billable hours by person this month", "Write-offs by matter"'
        className="h-9 bg-background"
      />
      <Button
        size="sm"
        className="h-9"
        disabled={!question.trim() || running}
        onClick={() => void handleAsk()}
      >
        {running ? <Loader2 className="size-4 animate-spin" /> : "Build"}
      </Button>
    </div>
  );
}
